import React, { useState, useEffect } from 'react';
import { Card, Button, Callout, Intent, Spinner, Divider, Text, FormGroup, InputGroup, Switch } from '@blueprintjs/core';
import { githubApi } from '../../api/github';

interface GithubIntegrationProps {
  organizationId?: string;
  onConnectionChange?: (connected: boolean) => void;
}

/**
 * Main GitHub Integration component that handles connection status and management
 * Shows connection status, allows connecting/disconnecting GitHub
 */
const GithubIntegration: React.FC<GithubIntegrationProps> = ({ organizationId, onConnectionChange }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [connected, setConnected] = useState<boolean>(false);
  const [checkingStatus, setCheckingStatus] = useState(true);
  const [backendAvailable, setBackendAvailable] = useState<boolean>(true);
  const [urlInfo, setUrlInfo] = useState<any>(null);
  const [connectionInfo, setConnectionInfo] = useState<any>(null);
  
  // PAT mode state
  const [usePat, setUsePat] = useState<boolean>(false);
  const [patToken, setPatToken] = useState<string>('');
  const [patLoading, setPatLoading] = useState(false);

  // Check connection status on mount
  useEffect(() => {
    checkConnectionStatus();
  }, []);

  // Notify parent of connection changes
  useEffect(() => {
    onConnectionChange?.(connected);
  }, [connected, onConnectionChange]);

  const checkConnectionStatus = async () => {
    setCheckingStatus(true);
    setError(null);
    try {
      const data = await githubApi.isConnected();
      // Now data is always an object with connected, installationId, urls properties
      setConnected(data.connected || false);
      setUrlInfo(data.urls || null);
      setConnectionInfo(data);
      setUsePat(data.usePat || false);
      setBackendAvailable(true);
    } catch (err: any) {
      console.warn('Failed to check GitHub connection status:', err);
      setConnected(false);
      
      // Check if this is a backend connectivity issue
      if (err.message?.includes('Backend server unavailable') || 
          err.message?.includes('Cannot connect to backend server')) {
        setBackendAvailable(false);
        setError('Backend server is not available. Please ensure the forethought-backend is running.');
      } else {
        setError(`Connection check failed: ${err.message}`);
      }
    } finally {
      setCheckingStatus(false);
    }
  };

  const handleConnect = async () => {
    setLoading(true);
    setError(null);
    try {
      const url = await githubApi.getInstallUrl();
      if (url) {
        // Open GitHub App installation in new window
        window.open(url, '_blank', 'width=800,height=600');
        
        // Set up listener for when user comes back
        const checkInterval = setInterval(async () => {
          try {
            const connectionData = await githubApi.isConnected();
            // Fix: Check the connected property, not the whole object
            if (connectionData && connectionData.connected === true) {
              setConnected(true);
              setConnectionInfo(connectionData);
              setUrlInfo(connectionData.urls || null);
              setBackendAvailable(true);
              clearInterval(checkInterval);
              console.log('✅ GitHub connection detected!');
            }
          } catch (err) {
            // Continue checking, but update backend status if needed
            if (err instanceof Error && err.message?.includes('Backend server unavailable')) {
              setBackendAvailable(false);
            }
          }
        }, 2000);

        // Clean up after 5 minutes
        setTimeout(() => clearInterval(checkInterval), 300000);
      } else {
        setError('Install URL not available');
      }
    } catch (err: any) {
      if (err.message?.includes('Backend server unavailable') || 
          err.message?.includes('Cannot connect to backend server')) {
        setBackendAvailable(false);
        setError('Backend server is not available. Please ensure the forethought-backend is running.');
      } else {
        setError(err.message || 'Failed to fetch install URL');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    checkConnectionStatus();
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Are you sure you want to disconnect GitHub? You will need to reinstall the app to reconnect.')) {
      return;
    }

    setLoading(true);
    setError(null);
    try {
      await githubApi.disconnect();
      setConnected(false);
      setConnectionInfo(null);
      setUrlInfo(null);
      // Refresh status to ensure UI is up to date
      setTimeout(checkConnectionStatus, 500);
    } catch (err: any) {
      setError(err.message || 'Failed to disconnect GitHub');
    } finally {
      setLoading(false);
    }
  };

  const handleSetPAT = async () => {
    if (!patToken.trim()) {
      setError('Please enter a Personal Access Token');
      return;
    }

    setPatLoading(true);
    setError(null);
    try {
      await githubApi.setPAT(patToken.trim());
      setPatToken(''); // Clear input after success
      await checkConnectionStatus();
    } catch (err: any) {
      setError(err.message || 'Failed to set Personal Access Token');
    } finally {
      setPatLoading(false);
    }
  };

  const handleRemovePAT = async () => {
    if (!window.confirm('Remove Personal Access Token?')) return;

    setPatLoading(true);
    setError(null);
    try {
      await githubApi.removePAT();
      await checkConnectionStatus();
    } catch (err: any) {
      setError(err.message || 'Failed to remove Personal Access Token');
    } finally {
      setPatLoading(false);
    }
  };

  if (checkingStatus) {
    return (
      <Card elevation={2} style={{ padding: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Spinner size={16} />
          <span>Checking GitHub connection...</span>
        </div>
      </Card>
    );
  }

  return (
    <Card elevation={2} style={{ padding: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {/* GitHub logo SVG */}
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path fillRule="evenodd" clipRule="evenodd" d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0024 12c0-6.63-5.37-12-12-12z" fill="currentColor"/>
          </svg>
          <h3 style={{ margin: 0 }}>GitHub Integration</h3>
        </div>
        <Button 
          minimal 
          icon="refresh" 
          onClick={handleRefresh}
          title="Refresh connection status"
        />
      </div>

      <p style={{ marginBottom: 16, color: '#5c7080' }}>
        Connect Forethought to your GitHub organization to link repositories to nodes and sync development data.
      </p>

      {error && (
        <Callout intent={Intent.DANGER} icon="error" style={{ marginBottom: 16 }}>
          {error}
          {!backendAvailable && (
            <div style={{ marginTop: 8, fontSize: 13 }}>
              <strong>To start the backend:</strong>
              <br />• Navigate to the forethought-backend directory
              <br />• Run: <code>npm start</code> or <code>yarn start</code>
              <br />• Ensure it's running on port 3000
            </div>
          )}
        </Callout>
      )}

      {!backendAvailable && !error && (
        <Callout intent={Intent.WARNING} icon="warning-sign" style={{ marginBottom: 16 }}>
          <strong>Backend Unavailable</strong>
          <div style={{ marginTop: 8, fontSize: 13 }}>
            The forethought-backend server is not running. GitHub integration requires the backend to be available.
          </div>
        </Callout>
      )}

      {/* Personal Access Token Section */}
      <div style={{ marginBottom: 20, padding: 16, backgroundColor: 'rgba(138, 155, 168, 0.05)', borderRadius: 6 }}>
        <Text style={{ fontSize: 14, fontWeight: 600, marginBottom: 12, display: 'block' }}>
          🔑 Personal Access Token (Alternative)
        </Text>
        <Text style={{ fontSize: 12, marginBottom: 12, display: 'block', color: '#5c7080' }}>
          Use a GitHub Personal Access Token instead of the GitHub App for simpler setup. 
          <a href="https://github.com/settings/tokens" target="_blank" rel="noopener noreferrer" style={{ marginLeft: 4 }}>
            Generate a token here →
          </a>
        </Text>
        
        {usePat && connectionInfo?.hasPat ? (
          <div>
            <Callout intent={Intent.SUCCESS} icon="tick" style={{ marginBottom: 10 }}>
              Personal Access Token is configured and active
            </Callout>
            <Button
              intent="danger"
              icon="cross"
              onClick={handleRemovePAT}
              loading={patLoading}
              disabled={patLoading}
              small
            >
              Remove Token
            </Button>
          </div>
        ) : (
          <FormGroup
            label="GitHub Personal Access Token"
            helperText="Needs 'repo' and 'read:org' scopes"
          >
            <div style={{ display: 'flex', gap: 8 }}>
              <InputGroup
                type="password"
                placeholder="ghp_xxxxxxxxxxxxxxxxxxxx"
                value={patToken}
                onChange={(e) => setPatToken(e.target.value)}
                disabled={patLoading}
                style={{ flex: 1 }}
              />
              <Button
                intent="primary"
                icon="key"
                onClick={handleSetPAT}
                loading={patLoading}
                disabled={patLoading || !patToken.trim()}
              >
                Set Token
              </Button>
            </div>
          </FormGroup>
        )}
      </div>

      <Divider style={{ margin: '20px 0' }} />

      {connected ? (
        <>
          <Callout intent={Intent.SUCCESS} icon="tick" style={{ marginBottom: 16 }}>
            <strong>GitHub Connected</strong>
            <div style={{ marginTop: 8, fontSize: 13 }}>
              {connectionInfo?.organizationName 
                ? `Connected to ${connectionInfo.organizationName} organization`
                : 'Your GitHub organization is connected to Forethought'
              }. You can now link repositories to nodes.
              {connectionInfo?.connectedAt && (
                <div style={{ marginTop: 4, opacity: 0.8 }}>
                  Connected on {new Date(connectionInfo.connectedAt).toLocaleString()}
                </div>
              )}
            </div>
          </Callout>
          
          <Divider style={{ margin: '16px 0' }} />
          
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <Button 
              intent="primary" 
              icon="cog" 
              onClick={() => window.open('https://github.com/settings/installations', '_blank')}
            >
              Manage Installation
            </Button>
            <Button 
              icon="refresh" 
              onClick={handleRefresh}
            >
              Refresh Status
            </Button>
            <Button 
              intent="danger" 
              icon="th-disconnect" 
              onClick={handleDisconnect}
              disabled={loading}
            >
              Disconnect
            </Button>
          </div>
          
          {urlInfo && (
            <div style={{ marginTop: 16, padding: 12, backgroundColor: 'rgba(138, 155, 168, 0.1)', borderRadius: 4 }}>
              <Text style={{ fontSize: 12, fontWeight: 600, marginBottom: 8, display: 'block' }}>
                Configuration Details:
              </Text>
              <div style={{ fontSize: 11, fontFamily: 'monospace', color: '#5c7080' }}>
                <div>Installation ID: {connectionInfo?.installationId}</div>
                <div>Backend: {urlInfo.backendUrl}</div>
                <div>Frontend: {urlInfo.frontendUrl}</div>
                <div>Install URL: {urlInfo.installUrl}</div>
              </div>
            </div>
          )}
        </>
      ) : (
        <div>
          <Callout intent={Intent.WARNING} icon="warning-sign" style={{ marginBottom: 16 }}>
            GitHub is not connected. Install the Forethought GitHub App to link repositories.
          </Callout>
          
          <Button 
            intent="primary" 
            icon="git-branch" 
            onClick={handleConnect} 
            disabled={loading || !backendAvailable}
            style={{ marginRight: 10 }}
          >
            {loading ? <Spinner size={16} /> : 'Connect GitHub'}
          </Button>
          
          <Button 
            icon="help" 
            onClick={() => window.open('https://docs.github.com/en/developers/apps/managing-github-apps/installing-github-apps', '_blank')}
          >
            Learn More
          </Button>
        </div>
      )}
    </Card>
  );
};

export default GithubIntegration; 